import React from "react"

import { NotificationManager } from "react-notifications"

import { Tooltip } from "@chakra-ui/core"
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid"

import classNames from "src/classNames"
import commaList from "src/commaList"
import dayjs from "src/dayjs"

import { gql, useMutation } from "hooks/urql"

import LocalTime from "components/LocalTime"
import Avatar from "components/Messaging/Avatar"

export type MessageProps = {
  message: {
    id: string
    hidden: boolean
    content: string
    createdAt: string
    author: {
      id: string
      showPath: string
      type: "Teacher" | "Student" | "Tutor"
      messagingDisplayName: string
      avatar: {
        thumbUrl
      }
    }
    attachment?: {
      filename: string
      downloadUrl: string
      url: string
      thumbUrl: string
      previewUrl: string
    }
    readRecipients: {
      id: string
      messagingDisplayName: string
    }[]
  }
}

const Message: React.FC<MessageProps> = ({ message }) => {
  const readNames = message.readRecipients.map(
    user => user.messagingDisplayName
  )
  const [, runMutation]: any[] = useMutation(hideMessageMutation)

  const hideMessage = () => {
    runMutation({ messageId: message.id, hide: !message.hidden })
      .then(
        result => {
          const { failures } = result.data.hideMessage
          if (failures.length > 0) {
            NotificationManager.error(failures[0].message)
          } else {
            NotificationManager.success(
              `This message is now ${message.hidden ? "visible" : "hidden"}`
            )
          }
        },
        () => NotificationManager.error("Something went wrong")
      )
      .catch(() => NotificationManager.error("Something went wrong"))
  }

  return (
    <div className="mb-4">
      <div className="flex items-start gap-2.5">
        <a
          className="cursor-pointer"
          href={message.author.showPath}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Avatar
            src={message.author.avatar.thumbUrl}
            alt={message.author.messagingDisplayName}
          />
        </a>

        <div
          className={classNames(
            "leading-1.5 flex w-full max-w-full flex-col rounded-tr-xl rounded-br-xl rounded-bl-xl border border-solid !border-gray-400 py-2 px-2 md:!px-4",
            message.hidden ? "bg-brand-100" : "bg-white"
          )}
        >
          <div className="flex justify-between">
            <div className="flex w-full flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2">
              <span className="text-base font-semibold text-gray-900">
                {message.author.messagingDisplayName}
              </span>
              <Tooltip
                label={`${dayjs(message.createdAt).format(
                  "MMM D, YYYY h:mma"
                )}`}
                className="rounded-md bg-gray-600 py-1 px-2 text-white"
                aria-label={`Time sent: ${dayjs(message.createdAt).format(
                  "MMM D, YYYY h:mma"
                )}}`}
              >
                <span className="cursor-help text-base font-normal text-gray-500">
                  <LocalTime timestamp={message.createdAt} relative />
                </span>
              </Tooltip>
            </div>
            <div className="justify-self-end">
              <button
                className={classNames(
                  "m-0 flex cursor-pointer items-center justify-center rounded-md border-4 border-solid border-brand px-2 focus:outline-none",
                  message.hidden
                    ? "!bg-brand-600 text-white hover:!bg-transparent hover:!text-brand-600"
                    : "bg-transparent text-brand-600 hover:!bg-brand-600 hover:text-white"
                )}
                onClick={hideMessage}
              >
                {message.hidden ? (
                  <EyeIcon className="h-6 w-6" />
                ) : (
                  <EyeSlashIcon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          <p className="py-2.5 text-base font-normal text-gray-900">
            {message.content}
          </p>
          {message.attachment.filename && (
            <>
              <img
                src={message.attachment.previewUrl}
                alt={message.attachment.filename}
                className="max-w-200px sm:max-w-[340px]"
              />
              <div className="my-2">
                <a href={message.attachment.downloadUrl}>
                  Download {message.attachment.filename}
                </a>
              </div>
            </>
          )}
          <span className="text-sm font-normal text-gray-500">
            {readNames.length > 0
              ? `Read by ${commaList(readNames)}`
              : "Unread"}
          </span>
        </div>
      </div>
    </div>
  )
}

const hideMessageMutation = gql`
  mutation HideMessage($messageId: ID!, $hide: Boolean!) {
    hideMessage(messageId: $messageId, hide: $hide) {
      failures {
        message
      }
      message {
        id
      }
    }
  }
`

export default Message
